/**
 * Loved-One Voice Query Hooks
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './keys';
import {
  familyVoicesService,
  type CreateFamilyVoiceInput,
  type FamilyVoice,
  type FamilyVoiceList,
} from '@/services/familyVoices';

/** Every cloned voice this family holds, plus the consent wording to display. */
export function useFamilyVoices(enabled = true) {
  return useQuery({
    queryKey: queryKeys.familyVoices.list(),
    queryFn: () => familyVoicesService.listFamilyVoices(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateFamilyVoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFamilyVoiceInput) => familyVoicesService.createFamilyVoice(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.familyVoices.all });
      // A new voice consumes plan allowance, so the usage meters are now stale.
      qc.invalidateQueries({ queryKey: queryKeys.billing.entitlements() });
    },
  });
}

export function useDeleteFamilyVoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => familyVoicesService.deleteFamilyVoice(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.familyVoices.all });
      qc.invalidateQueries({ queryKey: queryKeys.billing.entitlements() });
    },
  });
}

export type { FamilyVoice, FamilyVoiceList, CreateFamilyVoiceInput };
